// Picking labels for a Case (any number): a search over the person's labels,
// with "create a label named …" at the end when no label has the name typed (names are unique regardless of case). Another part's labels
// (a workspace's, for its issues) are picked the same way by passing where
// they come from (`source`).
//
// Promise:
// - Arrow keys move through the rows, Enter takes the one under the cursor
//   (not while composing: the Enter that settles a conversion makes nothing).
// - One label (`multiple` false): taking a row picks it and closes; the first
//   row while nothing is typed takes the label off (`onClear`).
// - Any number: taking a row adds or removes it and the panel stays, the
//   search emptied, for the next one.
// - Creating keeps the panel until the server made the label, then picks it
//   (never sending a label id the server has not given). A refusal shows in
//   the panel, under the search, naming the label; what was typed stays.
// - A label with a color shows it as a dot in place of the label mark.
import { m } from "@trameria/i18n";
import { type KeyboardEvent, type RefObject, useId, useState } from "react";
import { apiErrorWithLead } from "../data/errors";
import { isOptimisticId } from "../data/optimistic";
import type { ShellMenuSeparator } from "../shell/outside/contract";
import type { ShellActionInput } from "../shell/outside/outside";
import { ErrorLine } from "../ui/bits";
import { Icon } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { Popover } from "../ui/popover";
import { toast } from "../ui/toast";
import { LABEL_NAME_MAX, useCreateLabel, useLabels } from "./queries";

export interface PickedLabel {
  id: string;
  name: string;
}

/** A label as the pick lists it. */
export interface PickableLabel {
  id: string;
  name: string;
  /** `#rrggbb`: drawn as a dot. */
  color?: string;
}

/**
 * Where the labels come from and how a new one is made. Both are hooks: pass
 * one object made once (a module's constant), never one made while drawing.
 */
export interface LabelSource {
  useLabels(): {
    data: readonly PickableLabel[] | undefined;
    isSuccess: boolean;
  };
  useCreate(): {
    mutate(
      input: { name: string },
      options: {
        onSuccess(made: PickableLabel): void;
        onError(error: unknown): void;
      },
    ): void;
    isPending: boolean;
  };
  /** The longest name the server takes. */
  nameMax: number;
}

/** The person's own labels (Cases and People). */
export const PERSONAL_LABELS: LabelSource = {
  useLabels,
  useCreate: useCreateLabel,
  nameMax: LABEL_NAME_MAX,
};

type Row =
  | { kind: "label"; label: PickableLabel }
  | { kind: "create"; name: string }
  | { kind: "clear" };

export function LabelPickPopover({
  anchor,
  label,
  multiple,
  selected,
  clearLabel,
  onClose,
  onPick,
  onClear,
  source = PERSONAL_LABELS,
}: {
  anchor: RefObject<HTMLElement | null>;
  /** Read aloud for the panel and its search. */
  label: string;
  multiple: boolean;
  /** The ids on the thing now. */
  selected: readonly string[];
  /** The row that takes the label off (one label only). */
  clearLabel?: string;
  onClose(): void;
  /** A label was taken (with `multiple`, again to take it off). */
  onPick(label: PickedLabel): void;
  onClear?(): void;
  /** Whose labels (default: the person's own). */
  source?: LabelSource;
}) {
  const labels = source.useLabels();
  const create = source.useCreate();
  const nameMax = source.nameMax;
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const q = query.trim();
  const all = (labels.data ?? []).filter((l) => !isOptimisticId(l.id));
  const matches = all.filter((l) =>
    l.name.toLowerCase().includes(q.toLowerCase()),
  );
  const exact = all.some((l) => l.name.toLowerCase() === q.toLowerCase());
  const rows: Row[] = [
    ...(!multiple && onClear && selected.length > 0 && q === ""
      ? [{ kind: "clear" as const }]
      : []),
    ...matches.map((l) => ({
      kind: "label" as const,
      label: { id: l.id, name: l.name, color: l.color },
    })),
    ...(q !== "" && !exact
      ? [{ kind: "create" as const, name: q.slice(0, nameMax) }]
      : []),
  ];
  const listId = useId();
  const active = Math.min(cursor, rows.length - 1);
  const optionId = (i: number) => `${listId}-${i}`;
  const picked = (l: PickedLabel) => {
    onPick(l);
    if (multiple) {
      setQuery("");
      setCursor(0);
    } else onClose();
  };
  const choose = (row: Row) => {
    if (create.isPending) return;
    setError(null);
    if (row.kind === "label")
      picked({ id: row.label.id, name: row.label.name });
    else if (row.kind === "clear") {
      onClear?.();
      onClose();
    } else
      create.mutate(
        { name: row.name },
        {
          onSuccess: (made) => picked({ id: made.id, name: made.name }),
          onError: (err) =>
            setError(
              apiErrorWithLead(err, m.labels_create_failed({ name: row.name })),
            ),
        },
      );
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isImeComposing(e.nativeEvent)) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (rows.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setCursor((Math.max(active, 0) + step + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[active];
      if (row) choose(row);
    }
  };
  return (
    <Popover
      anchor={anchor}
      open
      onClose={onClose}
      label={label}
      className="w-[260px] max-w-[calc(100vw-16px)]"
    >
      <div className="px-2 pt-1 pb-1.5">
        <input
          type="text"
          role="combobox"
          aria-label={label}
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? optionId(active) : undefined}
          aria-autocomplete="list"
          placeholder={m.labels_pick_placeholder()}
          value={query}
          maxLength={nameMax}
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(0);
            setError(null);
          }}
          onKeyDown={onKeyDown}
          // biome-ignore lint/a11y/noAutofocus: opened to search
          autoFocus
          className="plain-field w-full rounded-[6px] border border-field bg-surface px-2 py-[5px] text-[12.5px] text-body outline-none focus:border-field-strong"
        />
        {error && <ErrorLine>{error}</ErrorLine>}
      </div>
      <div id={listId} role="listbox" aria-label={m.labels_title()}>
        {labels.isSuccess && all.length === 0 && q === "" && (
          <p className="px-3 py-2 text-[12px] leading-[1.6] text-faint">
            {m.labels_pick_empty()}
          </p>
        )}
        {rows.map((row, i) => {
          const on = row.kind === "label" && selected.includes(row.label.id);
          return (
            <button
              key={row.kind === "label" ? row.label.id : row.kind}
              id={optionId(i)}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={i === active}
              aria-checked={row.kind === "label" ? on : undefined}
              aria-disabled={row.kind === "create" && create.isPending}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(row);
              }}
              onMouseEnter={() => setCursor(i)}
              className={`flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-[13px] ${
                i === active ? "bg-hover" : ""
              } ${row.kind === "clear" ? "text-faint" : on ? "text-accent-deep" : "text-body"}`}
            >
              {row.kind === "label" && (
                <>
                  {row.label.color ? (
                    <span
                      aria-hidden="true"
                      className="flex w-[13px] flex-none justify-center"
                    >
                      <span
                        className="h-[7px] w-[7px] rounded-full"
                        style={{ background: row.label.color }}
                      />
                    </span>
                  ) : (
                    <Icon kind="label" size={13} className="flex-none" />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {row.label.name}
                  </span>
                  {on && <Icon kind="check" size={14} className="flex-none" />}
                </>
              )}
              {row.kind === "create" && (
                <>
                  <Icon
                    kind="plus"
                    size={13}
                    className="flex-none text-muted"
                  />
                  <span className="min-w-0 truncate">
                    {m.labels_create_named({ name: row.name })}
                  </span>
                </>
              )}
              {row.kind === "clear" && <span>{clearLabel}</span>}
            </button>
          );
        })}
      </div>
    </Popover>
  );
}

/**
 * The same pick as a menu's items, for a host that draws the chip's menu
 * itself (shell/outside): the labels (the ones on it checked, each with its
 * color; taking one again takes it off) and making one from a name the host
 * asks for (a name that is already a label picks that one). A refusal is a
 * note. Nothing is read until a host asks (`enabled`).
 */
export function useLabelMenuItems({
  enabled,
  selected,
  onPick,
  source = PERSONAL_LABELS,
}: {
  enabled: boolean;
  selected: readonly string[];
  onPick(label: PickedLabel): void;
  source?: LabelSource;
}): (ShellActionInput | ShellMenuSeparator)[] | undefined {
  const labels = source.useLabels();
  const create = source.useCreate();
  if (!enabled || !labels.isSuccess) return undefined;
  const all = (labels.data ?? []).filter((l) => !isOptimisticId(l.id));
  const make = (text?: string) => {
    const name = (text ?? "").trim().slice(0, source.nameMax);
    if (name === "" || create.isPending) return;
    const same = all.find((l) => l.name.toLowerCase() === name.toLowerCase());
    if (same) return onPick({ id: same.id, name: same.name });
    create.mutate(
      { name },
      {
        onSuccess: (made) => onPick({ id: made.id, name: made.name }),
        onError: (err) =>
          toast(apiErrorWithLead(err, m.labels_create_failed({ name }))),
      },
    );
  };
  return [
    ...all.map(
      (l): ShellActionInput => ({
        id: `label:${l.id}`,
        label: l.name,
        on: selected.includes(l.id),
        ...(l.color ? { color: l.color } : {}),
        onPress: () => onPick({ id: l.id, name: l.name }),
      }),
    ),
    ...(all.length > 0 ? [{ type: "separator" as const }] : []),
    {
      id: "label:new",
      label: m.labels_new(),
      icon: "plus",
      ...(create.isPending ? { disabled: true } : {}),
      prompt: {
        title: m.labels_new(),
        placeholder: m.labels_name(),
        max: source.nameMax,
      },
      onPress: make,
    },
  ];
}
